import React, { useState } from "react";
import api from "../../../lib/api";
import { toast } from "sonner";
import { Store, RefreshCw } from "lucide-react";

// Catalog Fulfillment PR 4 (Aug 2026) — tenant operational lifecycle UI
// for an Online Customer Pickup order (the PR3-implemented `pickup_phase`
// axis). Rendered inside OrderDetailsDrawer ONLY when `orderDoc?.
// pickup_phase` is present — entirely separate from the existing kiosk/
// walk-in Pickup workflow (which has no `pickup_phase` field at all and
// is untouched by this component) and from the Own Delivery panel.
//
// Reads `pickup_phase`/`pickup_snapshot`/`pickup_cancellation_reason`
// directly off the ALREADY-FETCHED `orderDoc` (no separate GET — these
// are plain order-document fields) and calls the parent's `onUpdated`
// (the drawer's own `load()`) after a successful transition so the
// displayed phase reflects the server's new state. `pickup_snapshot`
// is the checkout-time-immutable store address/instructions capture —
// this panel NEVER re-fetches current catalog data as a substitute.

const PHASE_LABEL = {
  accepted: "Accepted",
  preparing: "Preparing",
  ready_for_pickup: "Ready for Pickup",
  picked_up: "Picked Up",
  pickup_cancelled: "Pickup Cancelled",
};

const PHASE_COLOR = {
  accepted: "text-slate-700 bg-slate-100 border-slate-200",
  preparing: "text-sky-700 bg-sky-50 border-sky-200",
  ready_for_pickup: "text-indigo-700 bg-indigo-50 border-indigo-200",
  picked_up: "text-emerald-700 bg-emerald-50 border-emerald-200",
  pickup_cancelled: "text-rose-700 bg-rose-50 border-rose-200",
};

const NEXT_PHASES = {
  accepted: ["preparing", "pickup_cancelled"],
  preparing: ["ready_for_pickup", "pickup_cancelled"],
  ready_for_pickup: ["picked_up", "pickup_cancelled"],
  picked_up: [],
  pickup_cancelled: [],
};

const TERMINAL_PHASES = new Set(["picked_up", "pickup_cancelled"]);

const ACTION_LABEL = {
  preparing: "Start Preparing",
  ready_for_pickup: "Mark Ready for Pickup",
  picked_up: "Mark Picked Up",
  pickup_cancelled: "Cancel Order",
};

export default function PickupOperationalPanel({ orderId, orderDoc, onUpdated }) {
  const [submitting, setSubmitting] = useState(false);
  const [pendingCancel, setPendingCancel] = useState(false);
  const [cancellationReason, setCancellationReason] = useState("");

  const phase = orderDoc?.pickup_phase;
  if (!phase) return null;

  const snapshot = orderDoc?.pickup_snapshot || {};
  const storeAddress = snapshot.store_address || null;
  const instructions = snapshot.instructions || null;
  const nextPhases = NEXT_PHASES[phase] || [];
  const isTerminal = TERMINAL_PHASES.has(phase);

  const submitTransition = async (toPhase, extra = {}) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/retail/orders/${orderId}/pickup/transition`, {
        to_phase: toPhase, ...extra,
      });
      if (data.status === "already_at_phase") {
        toast.message("This order's pickup status was already updated.");
      } else {
        toast.success(`Marked as ${PHASE_LABEL[toPhase] || toPhase}.`);
      }
      setPendingCancel(false);
      setCancellationReason("");
      onUpdated && onUpdated();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && typeof det === "object" && det.message) || "Could not update this order's pickup status.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section data-testid="pickup-operational-panel" className="space-y-4">
      <div className="flex items-center gap-2">
        <Store className="w-4 h-4 text-orange-600" />
        <div className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold">Online Customer Pickup</div>
        <span
          className={`ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold border ${PHASE_COLOR[phase] || "text-slate-600 bg-slate-100 border-slate-200"}`}
          data-testid="pickup-panel-phase"
        >
          {PHASE_LABEL[phase] || phase}
        </span>
        <button
          type="button"
          onClick={() => onUpdated && onUpdated()}
          className="ml-auto text-slate-400 hover:text-slate-700"
          title="Refresh"
          data-testid="pickup-panel-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
      </div>

      {(storeAddress || instructions) && (
        <div className="rounded-md border border-slate-200 p-3 bg-slate-50/60" data-testid="pickup-panel-snapshot">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
            Store address &amp; instructions (as shown to the customer at checkout)
          </div>
          {storeAddress && (
            <div className="text-sm text-slate-900" data-testid="pickup-panel-store-address">
              {[storeAddress.line1, storeAddress.line2, storeAddress.city, storeAddress.state, storeAddress.postal_code]
                .filter(Boolean).join(", ")}
            </div>
          )}
          {instructions && (
            <div className="text-xs text-slate-600 mt-1" data-testid="pickup-panel-instructions">{instructions}</div>
          )}
        </div>
      )}

      {phase === "pickup_cancelled" && orderDoc?.pickup_cancellation_reason && (
        <div className="rounded-md bg-rose-50 border border-rose-200 text-rose-800 text-xs px-3 py-2" data-testid="pickup-panel-cancellation-reason">
          {orderDoc.pickup_cancellation_reason}
        </div>
      )}

      {!isTerminal && (
        <div className="space-y-2" data-testid="pickup-panel-actions">
          {pendingCancel ? (
            <div className="rounded-md border border-rose-200 bg-rose-50 p-3 space-y-2" data-testid="pickup-panel-cancel-form">
              <div className="text-xs font-medium text-rose-900">Cancel this pickup order</div>
              <textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Reason (visible to your own team only)"
                data-testid="pickup-cancel-reason-input"
                className="w-full text-xs border border-slate-300 rounded-md px-2 py-1.5"
                rows={2}
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={submitting}
                  onClick={() => submitTransition("pickup_cancelled", { cancellation_reason: cancellationReason.trim() || undefined })}
                  data-testid="pickup-cancel-confirm"
                  className="flex-1 px-3 py-2 rounded-md bg-rose-600 text-white text-xs font-medium hover:bg-rose-700 disabled:opacity-60"
                >
                  {submitting ? "Cancelling…" : "Confirm Cancel Order"}
                </button>
                <button type="button" disabled={submitting} onClick={() => setPendingCancel(false)} className="px-3 py-2 rounded-md border border-slate-300 text-xs">
                  Keep Order
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {nextPhases.map((next) => {
                const isDestructive = next === "pickup_cancelled";
                return (
                  <button
                    key={next}
                    type="button"
                    disabled={submitting}
                    onClick={() => (isDestructive ? setPendingCancel(true) : submitTransition(next))}
                    data-testid={`pickup-action-${next}`}
                    className={`px-3 py-2 rounded-md text-xs font-medium disabled:opacity-60 ${
                      isDestructive
                        ? "border border-rose-300 text-rose-700 hover:bg-rose-50"
                        : "bg-emerald-600 text-white hover:bg-emerald-700"
                    }`}
                  >
                    {ACTION_LABEL[next]}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
